import * as enabledApi from '@shared/api/enabledPlayApi';
import { isLicensedProfile, isLicenseValidForProfile } from '@shared/constants/profile/ProfileUtils';
import IUserAccess from '@shared/models/components/IUserAccess';
import DeviceProfilesFilterRequest from '@shared/models/deviceProfiles/DeviceProfilesFilterRequest';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import { OSTypes } from '@shared/models/devices/OSTypes';
import { getLanguageOnly, getSupportedLocale } from '@shared/models/localization/Locales';
import IResult from '@shared/models/result/IResult';
import NotFoundResult from '@shared/models/result/NotFoundResult';
import SuccessResult from '@shared/models/result/SuccessResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import dayjs from 'dayjs';
import { useState } from 'react';
import { createContainer } from 'unstated-next';
import getProfileFeatures from '../getProfileFeatures';
import { publish } from '../helpers/EventCallback';
import { getOnLineStatus } from '../statusBanners/useNetworkStatusBanner';
import useStateRef from '../useStateRef';

const PROFILES_LAST_UPDATED_DATE = 'PROFILES_LAST_UPDATED_DATE';
const DEFAULT_PROFILES = 'DEFAULT_PROFILES';

// todo: offline storage of default profiles with in app sqlite database
// todo: might need to be place an upper limit or time out if we get to too many profiles
function usePublicProfilesContainer() {
    const [defaultProfiles, setDefaultProfiles, defaultProfilesRef] = useStateRef<UserDeviceProfileModel[]>([]);
    const [publicProfiles, setPublicProfiles, publicProfilesRef] = useStateRef<UserDeviceProfileModel[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);

    const loadPublicProfiles = async (
        locale: string,
        os: OSTypes,
        licenseAccess: IUserAccess,
        loadFromStorage: boolean = false
    ) => {
        const isOnline = getOnLineStatus();
        if (!isOnline || loadFromStorage) {
            initializeFromStorage(locale, licenseAccess);
            return;
        }

        const defaultProfilesResult = await loadDefaultProfiles(locale, os, licenseAccess);

        if (defaultProfilesResult?.resultType !== 'Ok') {
            initializeFromStorage(locale, licenseAccess);
        }
    };

    const initializeFromStorage = (locale: string, licenseAccess: IUserAccess) => {
        try {
            let localDefaultProfiles = loadDefaultProfilesFromStorage(locale, licenseAccess);
            updateLocalDefaultProfiles(localDefaultProfiles);
        } catch (e) {
            console.log(e);
        }
    };

    const loadDefaultProfilesFromStorage = (locale: string, licenseAccess: IUserAccess) => {
        let localDefaultProfiles: UserDeviceProfileModel[] = [];
        try {
            const localProfiles = localStorage.getItem(DEFAULT_PROFILES);
            if (localProfiles) {
                const parsedProfiles = JSON.parse(localProfiles);
                localDefaultProfiles = getFilteredProfiles(parsedProfiles, locale, licenseAccess);
            }
        } catch (e) {
            console.log(e);
        }
        return localDefaultProfiles;
    };

    const getFilteredProfiles = (profiles: UserDeviceProfileModel[], locale: string, licenseAccess: IUserAccess) => {
        let localProfiles = profiles.filter(
            (profile: UserDeviceProfileModel) =>
                isProfileLocaleMatch(profile, locale) && isLicenseValidForProfile(profile, licenseAccess)
        );
        return localProfiles;
    };

    const isProfileLocaleMatch = (profile: UserDeviceProfileModel, locale: string) => {
        if (!profile.defaultLocale) return true;

        const normalizedLocale = locale.toLowerCase();
        const profileLocale = profile.defaultLocale.toLowerCase();

        return (
            getSupportedLocale(profileLocale, 'voice') === normalizedLocale ||
            getLanguageOnly(profileLocale) === getLanguageOnly(normalizedLocale)
        );
    };

    /**
     * gets a paginated amount of public profiles at a time.
     * @param filters
     * @param os pass in getProcessOS if calling from desktop/render side
     * @returns
     */
    const getPaginatedPublicProfiles = async (
        filters: DeviceProfilesFilterRequest,
        os: OSTypes
    ): Promise<IResult<UserDeviceProfileModel[]>> => {
        try {
            setIsLoading(true);
            const request = { ...filters, platform: os };
            const result = await enabledApi.getPublicProfiles(request);

            if (result?.resultType == 'Ok') {
                const sortedProfiles =
                    result.data?.sort((a, b) => dayjs(b.modifiedDate).diff(dayjs(a.modifiedDate))) || [];

                sortedProfiles.map((p: UserDeviceProfileModel, i: number) => {
                    sortedProfiles[i].features = getProfileFeatures(p.configuration);
                });

                setPublicProfiles(sortedProfiles);
            }

            setIsLoading(false);
            return result;
        } catch (error) {
            console.error(error);
            setIsLoading(false);
            setErrors(['Error getting profiles']);
            return new UnexpectedResult<UserDeviceProfileModel[]>();
        }
    };

    /**
     * get a public profile by Id if it is not already loaded.
     * @param profileId
     * @returns
     */
    const getProfileById = async (profileId: string, loadFromStorageOnly: boolean = false) => {
        try {
            // public profile or hidden onboarding profile
            const existingProfile = defaultProfilesRef.current.find((p) => p.id == profileId);

            if (existingProfile) {
                return new SuccessResult<UserDeviceProfileModel>(existingProfile);
            } else if (loadFromStorageOnly) {
                return new NotFoundResult<UserDeviceProfileModel>();
            } else {
                const result = await enabledApi.getPublicProfileById(profileId);
                return result;
            }
        } catch (error) {
            return new UnexpectedResult<UserDeviceProfileModel>();
        }
    };

    const loadDefaultProfilesFromRemote = async (
        locale: string,
        os: OSTypes,
        lastUpdatedDate?: string | undefined | null
    ): Promise<UserDeviceProfileModel[]> => {
        const localDefaultProfiles: UserDeviceProfileModel[] = [];
        try {
            let skip = 0;
            const take = 25;
            let result: IResult<UserDeviceProfileModel[]> = new UnexpectedResult<UserDeviceProfileModel[]>();
            let request: DeviceProfilesFilterRequest = {
                locale,
                skip,
                take,
                isHidden: false,
                modifiedDate: lastUpdatedDate ? lastUpdatedDate : undefined,
                platform: os,
            };
            // load profiles via pagination
            let hasMore = true;
            while (hasMore) {
                result = await enabledApi.getPublicProfiles(request);

                if (result?.resultType === 'Ok' && result.data) {
                    if (result.data.length === 0 || result.data.length < take) {
                        hasMore = false;
                    }
                    localDefaultProfiles.push(...result.data);
                    skip += result.data.length;
                    request = { ...request, skip };
                } else {
                    setErrors(result.errors ?? ['Error loading default profiles']);
                    break;
                }
            }

            return localDefaultProfiles;
        } catch (error) {
            console.error(error);
            setErrors(['Error loading default profiles']);
            return localDefaultProfiles;
        } finally {
            setIsLoading(false);
        }
    };

    const saveDefaultProfilesToStorage = (profiles: UserDeviceProfileModel[]) => {
        try {
            localStorage.setItem(PROFILES_LAST_UPDATED_DATE, new Date().toISOString());
            localStorage.setItem(DEFAULT_PROFILES, JSON.stringify(profiles));
            updateLocalDefaultProfiles(profiles);
        } catch (error) {
            console.error(error);
        }
    };

    const isProfilesOutdated = (profiles: UserDeviceProfileModel[], locale: string, licenseAccess: IUserAccess) => {
        // no profiles
        if (profiles.length === 0) return true;

        // locale mismatch
        const newLocaleProfiles = profiles.filter((p) => isProfileLocaleMatch(p, locale));
        if (newLocaleProfiles.length === 0) return true;

        // license upgraded
        const freeProfiles = profiles.filter((p) => isLicensedProfile(p));
        if (freeProfiles.length === profiles.length && licenseAccess.type !== 'personal') {
            return true;
        }

        return false;
    };

    /**
     * Loads all available default and public profiles after a modified date. Does not get hidden profiles.
     * @param locale
     * @param os pass in getProcessOS if calling from desktop/render side
     * @returns that last result of the paginated profiles
     */
    const loadDefaultProfiles = async (
        locale: string,
        os: OSTypes,
        licenseAccess: IUserAccess
    ): Promise<IResult<UserDeviceProfileModel[]>> => {
        try {
            setIsLoading(true);

            let existingProfiles = loadDefaultProfilesFromStorage(locale, licenseAccess);

            const lastUpdatedDate =
                existingProfiles.length > 0 && !isProfilesOutdated(existingProfiles, locale, licenseAccess)
                    ? localStorage.getItem(PROFILES_LAST_UPDATED_DATE)
                    : undefined;

            const localDefaultProfiles = await loadDefaultProfilesFromRemote(locale, os, lastUpdatedDate);
            const combinedProfiles = [...localDefaultProfiles];

            if (localDefaultProfiles.length > 0 && existingProfiles) {
                // only add existing profiles if their locale matches
                for (const profile of existingProfiles) {
                    if (isProfileLocaleMatch(profile, locale)) {
                        const existingProfile = combinedProfiles.find((p) => p.id === profile.id);
                        if (!existingProfile) {
                            combinedProfiles.push(profile);
                        }
                    }
                }
            }

            if (combinedProfiles.length > 0) {
                saveDefaultProfilesToStorage(combinedProfiles);
            } else {
                updateLocalDefaultProfiles(existingProfiles);
            }

            return combinedProfiles.length > 0
                ? new SuccessResult<UserDeviceProfileModel[]>(combinedProfiles)
                : new SuccessResult<UserDeviceProfileModel[]>(existingProfiles);
        } catch (error) {
            console.error(error);
            setErrors(['Error loading default profiles']);
            return new UnexpectedResult<UserDeviceProfileModel[]>();
        } finally {
            setIsLoading(false);
        }
    };

    const getPublicProfileFeatures = (profileId: string) => {
        const profile = publicProfiles.find((p) => p.id === profileId);
        if (!profile) return [];
        const features = getProfileFeatures(profile?.configuration);
        return features;
    };

    const clearProfiles = () => {
        updateLocalDefaultProfiles([]);
        setPublicProfiles([]);
        localStorage.removeItem(DEFAULT_PROFILES);
        localStorage.removeItem(PROFILES_LAST_UPDATED_DATE);
    };

    const updateLocalDefaultProfiles = (profiles: UserDeviceProfileModel[]) => {
        const sorted = profiles.sort((a, b) => a.name.localeCompare(b.name));
        setDefaultProfiles(sorted);
        publish('default-profiles-updated', sorted);
    };

    const getProfile = async (profileId: string, loadFromStorageOnly: boolean = false) => {
        const profileResult = await getProfileById(profileId, loadFromStorageOnly);
        if (profileResult?.resultType === 'Ok' && profileResult.data) {
            return profileResult.data;
        }
        return null;
    };

    return {
        publicProfiles,
        publicProfilesRef,
        getPublicProfileFeatures,
        isLoading,
        errors,
        initializeFromStorage,
        loadPublicProfiles,
        defaultProfiles,
        defaultProfilesRef,
        loadDefaultProfilesFromRemote,
        saveDefaultProfilesToStorage,
        isProfileLocaleMatch,
        clearProfiles,
        getProfile,
        getPaginatedPublicProfiles,
    };
}

const PublicProfilesContainer = createContainer(usePublicProfilesContainer);
export default PublicProfilesContainer;
